/**
 * Gift Box Module
 * Handles gift box addition/removal and gift message management
 */

class GiftBoxManager {
  constructor() {
    this.giftBoxVariantId = null;
    this.isProcessing = false;

    // Get variant ID from any checkbox present on page load
    const firstCheckbox = document.querySelector('[data-gift-box-checkbox]');
    if (firstCheckbox) {
      this.giftBoxVariantId = firstCheckbox.dataset.giftBoxVariantId;
      this.init();
    }
  }

  init() {
    // Use event delegation for checkboxes (handles dynamically added elements)
    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-gift-box-checkbox]')) {
        this.handleCheckboxChange(event);
      }
    });

    // Use event delegation for message textareas
    document.addEventListener('blur', (event) => {
      if (event.target.matches('[data-gift-box-message]')) {
        this.handleMessageUpdate(event);
      }
    }, true); // Use capture phase for blur events

    // Use event delegation for toggle buttons
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-gift-box-toggle]')) {
        this.handleToggleClick(event);
      }
    });

    // Listen for cart updates to sync state
    document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));

    // Listen for gift box remove button clicks
    this.observeGiftBoxRemoveButton();
  }

  setLoadingState(isLoading) {
    const checkboxes = document.querySelectorAll('[data-gift-box-checkbox]');
    const loadingIndicators = document.querySelectorAll('[data-gift-box-loading]');

    checkboxes.forEach(checkbox => {
      if (isLoading) {
        // Store original disabled state
        checkbox.dataset.originalDisabled = checkbox.disabled;
        checkbox.disabled = true;
      } else {
        // Restore original disabled state
        checkbox.disabled = checkbox.dataset.originalDisabled === 'true';
        delete checkbox.dataset.originalDisabled;
      }
    });

    loadingIndicators.forEach(indicator => {
      indicator.style.display = isLoading ? 'inline-flex' : 'none';
    });
  }

  handleToggleClick(event) {
    event.preventDefault();
    const toggleButton = event.target.closest('[data-gift-box-toggle]');
    const wrapper = toggleButton.closest('[data-gift-box-wrapper]');
    const messageContainer = wrapper.querySelector('[data-gift-box-message-container]');

    if (messageContainer) {
      const isExpanded = messageContainer.classList.contains('gift-box-message-container--expanded');

      if (isExpanded) {
        // Collapse
        messageContainer.classList.remove('gift-box-message-container--expanded');
        toggleButton.classList.remove('gift-box-toggle--expanded');
      } else {
        // Expand
        messageContainer.style.display = 'block';
        setTimeout(() => {
          messageContainer.classList.add('gift-box-message-container--expanded');
          toggleButton.classList.add('gift-box-toggle--expanded');
        }, 10);
      }
    }
  }

  observeGiftBoxRemoveButton() {
    // Use event delegation since cart items may be re-rendered
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('cart-remove-button[data-gift-box-remove]');
      if (removeButton) {
        // Gift box is being removed - uncheck checkboxes and clear message
        this.handleGiftBoxRemoved();
      }
    });
  }

  async handleGiftBoxRemoved() {
    // Uncheck all checkboxes
    this.syncCheckboxStates(false);
    // Hide message containers
    this.toggleMessageContainers(false);
    // Clear the gift message attribute
    await this.removeGiftMessageAttribute();
  }

  async handleCheckboxChange(event) {
    if (this.isProcessing) return;

    const checkbox = event.target;
    const isChecked = checkbox.checked;
    const context = checkbox.dataset.context;

    this.isProcessing = true;
    this.setLoadingState(true);

    try {
      if (isChecked) {
        await this.addGiftBoxToCart(context);
      } else {
        await this.removeGiftBoxFromCart();
      }
    } catch (error) {
      console.error('Gift box error:', error);
      // Revert checkbox state on error
      checkbox.checked = !isChecked;
    } finally {
      this.isProcessing = false;
      this.setLoadingState(false);
    }
  }

  async addGiftBoxToCart(context) {
    try {
      const formData = {
        items: [{
          id: this.giftBoxVariantId,
          quantity: 1
        }]
      };

      console.log('gift box payload?', formData);

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to add gift box');
      }

      // Check if there's an existing gift message and apply it to the line item
      const cart = await this.getCart();
      if (cart.attributes && cart.attributes.gift_message) {
        const giftBoxItem = cart.items.find(item =>
          item.variant_id == this.giftBoxVariantId
        );

        if (giftBoxItem) {
          const lineItemData = {
            id: giftBoxItem.key,
            properties: {
              'Gift Message': cart.attributes.gift_message
            }
          };

          await fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(lineItemData)
          });
        }
      }

      await this.refreshCart();
      this.syncCheckboxStates(true);
      this.toggleMessageContainers(true);

      // Open cart drawer if adding from PDP
      if (context === 'pdp') {
        this.openCartDrawer();
      }
    } catch (error) {
      console.error('Error adding gift box:', error);
      throw error;
    }
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  }

  async removeGiftBoxFromCart() {
    try {
      // Get current cart to find gift box line item
      const cart = await this.getCart();
      const giftBoxItem = cart.items.find(item =>
        item.variant_id == this.giftBoxVariantId
      );

      if (!giftBoxItem) {
        this.syncCheckboxStates(false);
        return;
      }

      // Remove gift box from cart
      const formData = {
        id: giftBoxItem.key,
        quantity: 0
      };

      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to remove gift box');
      }

      // Remove gift message attribute
      await this.removeGiftMessageAttribute();

      await this.refreshCart();
      this.syncCheckboxStates(false);
      this.toggleMessageContainers(false);
    } catch (error) {
      console.error('Error removing gift box:', error);
      throw error;
    }
  }

  async handleMessageUpdate(event) {
    const message = event.target.value.trim();
    await this.updateGiftMessageAttribute(message);
  }

  async updateGiftMessageAttribute(message) {
    try {
      // Update cart attribute
      const cartAttributeData = {
        attributes: {
          gift_message: message
        }
      };

      const cartResponse = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartAttributeData)
      });

      if (!cartResponse.ok) {
        throw new Error('Failed to update gift message cart attribute');
      }

      // Also update the gift box line item property
      const cart = await this.getCart();
      const giftBoxItem = cart.items.find(item =>
        item.variant_id == this.giftBoxVariantId
      );

      if (giftBoxItem) {
        const lineItemData = {
          id: giftBoxItem.key,
          properties: {
            'Gift Message': message
          }
        };

        const lineItemResponse = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(lineItemData)
        });

        if (!lineItemResponse.ok) {
          throw new Error('Failed to update gift message line item property');
        }
      }
    } catch (error) {
      console.error('Error updating gift message:', error);
    }
  }

  async removeGiftMessageAttribute() {
    try {
      // Clear cart attribute
      const cartAttributeData = {
        attributes: {
          gift_message: ''
        }
      };

      const cartResponse = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartAttributeData)
      });

      if (!cartResponse.ok) {
        throw new Error('Failed to remove gift message cart attribute');
      }

      // Also clear the gift box line item property
      const cart = await this.getCart();
      const giftBoxItem = cart.items.find(item =>
        item.variant_id == this.giftBoxVariantId
      );

      if (giftBoxItem) {
        const lineItemData = {
          id: giftBoxItem.key,
          properties: {
            'Gift Message': ''
          }
        };

        const lineItemResponse = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(lineItemData)
        });

        if (!lineItemResponse.ok) {
          throw new Error('Failed to remove gift message line item property');
        }
      }

      // Clear textarea values
      const messageTextareas = document.querySelectorAll('[data-gift-box-message]');
      messageTextareas.forEach(textarea => {
        textarea.value = '';
      });
    } catch (error) {
      console.error('Error removing gift message:', error);
    }
  }

  async getCart() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  }

  async refreshCart() {
    // Get updated cart
    const cart = await this.getCart();

    // Check if gift box is only item and remove it
    if (cart.items.length === 1 && cart.items[0].variant_id == this.giftBoxVariantId) {
      await this.removeGiftBoxFromCart();
      return;
    }

    // Dispatch custom event for other components to listen
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

    // Update cart drawer by fetching sections
    await this.updateCartDrawer();

    // Update cart icon
    this.updateCartIcon(cart);
  }

  async updateCartDrawer() {
    // Fetch cart drawer section with updated content
    const sections = 'cart-drawer,cart-icon-bubble';
    const response = await fetch(`${window.Shopify.routes.root}?sections=${sections}`);
    const data = await response.json();

    // Update cart drawer HTML
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && data['cart-drawer']) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
      const newDrawerInner = doc.querySelector('.drawer__inner');
      const currentDrawerInner = cartDrawer.querySelector('.drawer__inner');

      if (newDrawerInner && currentDrawerInner) {
        currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      }
    }

    // Update cart icon bubble
    if (data['cart-icon-bubble']) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
      const newBubble = doc.querySelector('#cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');

      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }
    }
  }

  updateCartIcon(cart) {
    const cartIconBubbles = document.querySelectorAll('.cart-count-bubble');
    cartIconBubbles.forEach(bubble => {
      if (cart.item_count > 0) {
        bubble.textContent = cart.item_count;
        bubble.classList.remove('hidden');
      } else {
        bubble.classList.add('hidden');
      }
    });
  }

  syncCheckboxStates(isChecked) {
    const checkboxes = document.querySelectorAll('[data-gift-box-checkbox]');
    checkboxes.forEach(checkbox => {
      if (!checkbox.disabled) {
        checkbox.checked = isChecked;
      }
    });
  }

  toggleMessageContainers(show) {
    const messageContainers = document.querySelectorAll('[data-gift-box-message-container]');
    const toggleButtons = document.querySelectorAll('[data-gift-box-toggle]');

    // Show/hide toggle buttons
    toggleButtons.forEach(button => {
      button.style.display = show ? 'flex' : 'none';
      if (show) {
        button.classList.add('gift-box-toggle--expanded');
      } else {
        button.classList.remove('gift-box-toggle--expanded');
      }
    });

    // Show/hide message containers
    messageContainers.forEach(container => {
      if (show) {
        container.style.display = 'block';
        // Add a small delay for animation
        setTimeout(() => {
          container.classList.add('gift-box-message-container--expanded');
        }, 10);
      } else {
        container.classList.remove('gift-box-message-container--expanded');
        setTimeout(() => {
          container.style.display = 'none';
        }, 300); // Match CSS transition duration
      }
    });
  }

  async handleCartUpdate(event) {
    // Update checkbox states based on cart contents
    const cart = event.detail || await this.getCart();
    const hasGiftBox = cart.items.some(item => item.variant_id == this.giftBoxVariantId);

    this.syncCheckboxStates(hasGiftBox);
    this.toggleMessageContainers(hasGiftBox);

    // Enable/disable PDP checkbox based on cart state
    const pdpCheckbox = document.querySelector('[data-gift-box-checkbox][data-context="pdp"]');
    if (pdpCheckbox) {
      // Only enable if cart has items (excluding gift box)
      const nonGiftBoxItems = cart.items.filter(item => item.variant_id != this.giftBoxVariantId);
      pdpCheckbox.disabled = nonGiftBoxItems.length === 0;
    }

    // Update gift message textarea with current value
    if (cart.attributes && cart.attributes.gift_message) {
      const messageTextareas = document.querySelectorAll('[data-gift-box-message]');
      messageTextareas.forEach(textarea => {
        textarea.value = cart.attributes.gift_message;
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new GiftBoxManager();
  });
} else {
  new GiftBoxManager();
}
